import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { ClipboardCheck, Database, ShieldX, Users } from 'lucide-react';
import clsx from 'clsx';
import type { Permission, SessionUser } from '@idas/shared';
import { api } from '../lib/api';
import { ConnectionsTab } from './admin/ConnectionsTab';
import { PolicyTab } from './admin/PolicyTab';
import { UsersTab } from './admin/UsersTab';
import { ReviewTab } from './admin/ReviewTab';

const TABS = [
  { id: 'users', label: 'People', icon: Users, permission: 'user:read' },
  { id: 'policy', label: 'Policy', icon: ShieldX, permission: 'policy:read' },
  { id: 'review', label: 'Review', icon: ClipboardCheck, permission: 'policy:read' },
  { id: 'connections', label: 'Connections', icon: Database, permission: 'datasource:read' },
] as const satisfies ReadonlyArray<{
  id: string;
  label: string;
  icon: typeof Users;
  permission: Permission;
}>;

export function AdminPage({ user }: { user: SessionUser }) {
  const [params, setParams] = useSearchParams();

  // Tabs a given role cannot use are not rendered at all. An auditor holds
  // policy:read but not policy:write, so they see the rules and the review
  // queue read-only — which is exactly the access an auditor should have.
  const visibleTabs = TABS.filter((tab) => user.permissions.includes(tab.permission));
  const requested = params.get('tab');
  const active = visibleTabs.find((tab) => tab.id === requested) ?? visibleTabs[0];

  // Badge counts on the tabs themselves, so an admin who lands on Connections
  // still sees that three people are waiting for approval.
  const pending = useQuery({
    queryKey: ['users', 'pending'],
    queryFn: api.users.pending,
    enabled: user.permissions.includes('user:read'),
  });
  const unreviewed = useQuery({
    queryKey: ['catalog', 'unclassified', ''],
    queryFn: () => api.catalog.unclassified(),
    enabled: user.permissions.includes('policy:read'),
  });

  const badges: Record<string, number | undefined> = {
    users: pending.data?.users.length || undefined,
    review: unreviewed.data?.objects.length || undefined,
  };

  const badgeLoading: Record<string, boolean> = {
    users: pending.isLoading,
    review: unreviewed.isLoading,
  };

  if (!active) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-2xs text-ink-muted">
          Your role does not include any administrative access.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex h-9 shrink-0 items-center gap-0.5 border-b border-edge bg-surface-raised px-2">
        {visibleTabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            className={clsx(
              'flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
              id === active.id
                ? 'bg-accent/10 text-accent'
                : 'text-ink-muted hover:bg-surface-sunken hover:text-ink',
            )}
            onClick={() => setParams({ tab: id })}
          >
            <Icon className="h-3.5 w-3.5" strokeWidth={2} />
            {label}
            {/* A hollow pill while the count is still unknown, so the tab does
                not change width the moment it arrives. */}
            {badgeLoading[id] && (
              <span className="h-3.5 w-5 animate-pulse rounded-full bg-edge" />
            )}
            {!badgeLoading[id] && badges[id] !== undefined && (
              <span className="rounded-full bg-warn/20 px-1.5 text-2xs tabular-nums text-warn">
                {badges[id]}
              </span>
            )}
          </button>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-5xl">
          {active.id === 'users' && <UsersTab me={user} />}
          {active.id === 'policy' && <PolicyTab me={user} />}
          {active.id === 'review' && <ReviewTab me={user} />}
          {active.id === 'connections' && <ConnectionsTab me={user} />}
        </div>
      </div>
    </div>
  );
}
